import { Icon } from '@iconify/react';
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../../styles/Auth/DashBoard.css';
import { useSubscriptionRenewal } from '../../../context/SubscriptionRenewalContext.jsx';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { toast } from 'react-toastify';
import { getData, putData, postData } from '../../../webService/webservice.js';
import { useStore } from "../../../Context/context.jsx";
import StoreSingleAllStoresSelect, { ALL_STORE_OPTION } from "../../Challange/StoreSingleAllStoresSelect.jsx";
import Footer from '../Footer/Footer-page.jsx';

ChartJS.register(
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  ChartDataLabels
);

/* ── Top Metric Cards (row of 7) ─────────────────────────────────── */
const MetricCard = ({
  label,
  value,
  subValue,
  icon,
  colorClass,
  sparkData = [],
  onClick
}) => {


  const generateSparkData = (currentValue) => {
    const val = Number(currentValue || 0);

    return [
      Math.round(val * 0.45),
      Math.round(val * 0.55),
      Math.round(val * 0.60),
      Math.round(val * 0.75),
      Math.round(val * 0.85),
      Math.round(val * 0.95),
      val
    ];
  };

  return (

    <div
      className={`performance-metric-card neat-card`}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >

      {/* GRAPH BG */}
      <div className="metric-graph-bg">

        {sparkData.map((h, i) => (
          <div
            key={i}
            className="metric-bar"
            style={{
              height: `${Math.min(Math.max(h, 5), 45)}px`
            }}
          />
        ))}

      </div>

      <div className="metric-card-content">

        <div className="metric-card-left">

          <div
            className={`tile-icon-box ${colorClass.replace(
              '-bg',
              '-soft-bg'
            )}`}
          >
            <Icon
              icon={icon}
              style={{ fontSize: '40px' }}
            />
          </div>

          <span className="metric-card-value">
            {value}
          </span>

          <span className="metric-card-label">
            {label}
          </span>

          {subValue !== undefined && (
            <span
              className="metric-card-subvalue"
              style={{
                color:
                  subValue?.includes('↑')
                    ? '#16a34a'
                    : subValue?.includes('↓')
                      ? '#dc2626'
                      : '#64748b',
              }}
            >
              {subValue}
            </span>
          )}

        </div>

      </div>

    </div>
  );
};

/* ── Payments Summary Card ─────────────────────────────────────────── */
const PaymentSummaryCard = ({ total, data, onClick }) => {
  const calculatedTotal = (data?.totalCash || 0) + (data?.totalUPI || 0) + (data?.totalCard || 0) + (data?.totalWallet || 0) + (data?.totalOthers || 0);
  const effectiveTotal = calculatedTotal > 0 ? calculatedTotal : (total || 0);

  const donutData = {
    labels: ['Cash', 'UPI', 'Card', 'Wallet', 'Others'],
    datasets: [{
      data: [
        data?.totalCash || 0,
        data?.totalUPI || 0,
        data?.totalCard || 0,
        data?.totalWallet || 0,
        data?.totalOthers || 0
      ],
      backgroundColor: ['#7c3aed', '#3b82f6', '#ec4899', '#10b981', '#f59e0b'],
      borderWidth: 0,
      cutout: '72%',
      borderRadius: 4,
      hoverOffset: 8,
    }]
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 1500, easing: 'easeOutQuart' },
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#000000",
      },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.parsed.toLocaleString()}`
        }
      }
    }
  };

  const cashPct = effectiveTotal > 0 ? Math.round((data?.totalCash || 0) / effectiveTotal * 100) : 0;
  const upiPct = effectiveTotal > 0 ? Math.round((data?.totalUPI || 0) / effectiveTotal * 100) : 0;
  const cardPct = effectiveTotal > 0 ? Math.round((data?.totalCard || 0) / effectiveTotal * 100) : 0;
  const walletPct = effectiveTotal > 0 ? Math.round((data?.totalWallet || 0) / effectiveTotal * 100) : 0;
  const otherPct = effectiveTotal > 0 ? Math.round((data?.totalOthers || 0) / effectiveTotal * 100) : 0;

  return (
    <div
      className="summary-split-card card-vibrant"
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <div className="summary-card-header">
        <div className="header-icon-circle"><Icon icon="solar:card-bold-duotone" /></div>
        <span className="summary-card-title">Payments Summary</span>
      </div>
      <div className="summary-card-body">
        {/* Left: total + breakdown */}
        <div className="summary-card-left">
          <div className="summary-total-label">TOTAL PAYMENTS</div>
          <div className="summary-total-value"> {effectiveTotal.toLocaleString()}</div>
          <div className="summary-breakdown">
            {[
              {
                label: 'Cash',
                val: data?.totalCash || 0,
                icon: 'fluent-emoji:money-bag',
                color: '#16a34a'
              },
              {
                label: 'UPI',
                val: data?.totalUPI || 0,
                icon: 'fluent-emoji:mobile-phone',
                color: '#2563eb'
              },
              {
                label: 'Card',
                val: data?.totalCard || 0,
                icon: 'fluent-emoji:credit-card',
                color: '#db2777'
              },
              {
                label: 'Wallet',
                val: data?.totalWallet || 0,
                icon: 'fluent-emoji:purse',
                color: '#10b981'
              },
              {
                label: 'Others',
                val: data?.totalOthers || 0,
                icon: 'fluent-emoji:bank',
                color: '#f59e0b'
              },
            ].map(item => (
              <div className="summary-breakdown-row" key={item.label}>
                <div className="summary-item-left">
                  <div
                    className="summary-mini-icon"
                    style={{ background: item.color }}
                  >
                    <Icon icon={item.icon} />
                  </div>
                  <span>{item.label}</span>
                </div>
                <span> {item.val.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Center: Doughnut */}
        <div className="summary-donut-wrap">
          <div className="summary-donut-canvas">
            <Doughnut data={donutData} options={donutOptions} />
            <div className="donut-center-overlay">
              <span className="donut-center-total"> {effectiveTotal.toLocaleString()}</span>
              <span className="donut-center-sub">Total</span>
            </div>
          </div>
        </div>

        {/* Right: legend */}
        <div className="summary-legend">
          {[
            { label: 'Cash', pct: cashPct, color: '#7c3aed' },
            { label: 'UPI', pct: upiPct, color: '#3b82f6' },
            { label: 'Card', pct: cardPct, color: '#ec4899' },
            { label: 'Wallet', pct: walletPct, color: '#10b981' },
            { label: 'Others', pct: otherPct, color: '#f59e0b' },
          ].map(item => (
            <div className="summary-legend-item" key={item.label}>
              <span className="legend-dot" style={{ background: item.color }}></span>
              <span className="legend-label">{item.label}</span>
              <span className="legend-pct">{item.pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Wallet Summary Card ──────────────────────────────────────────── */
const WalletSummaryCard = ({ data }) => {
  const cashWallet = data?.cashWallet !== undefined ? data.cashWallet : (data?.totalCashWallet || 0);
  const serviceWallet = data?.serviceWallet !== undefined ? data.serviceWallet : (data?.totalServiceWallet || 0);

  const calculatedTotal = cashWallet + serviceWallet;
  const effectiveTotal = calculatedTotal > 0 ? calculatedTotal : (data?.totalWalletSummary || data?.totalWallet || 0);

  const cashPct = effectiveTotal > 0 ? Math.round((cashWallet / effectiveTotal) * 100) : 0;
  const servicePct = effectiveTotal > 0 ? Math.round((serviceWallet / effectiveTotal) * 100) : 0;

  const donutData = {
    labels: ['Cash Wallet', 'Service Wallet'],
    datasets: [{
      data: effectiveTotal > 0 ? [cashWallet, serviceWallet] : [1],
      backgroundColor: effectiveTotal > 0 ? ['#10b981', '#8b5cf6'] : ['#e2e8f0'],
      borderWidth: 0,
      cutout: '72%',
      borderRadius: 4,
      hoverOffset: 4,
    }]
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 1500, easing: 'easeOutQuart' },
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#000000",
        formatter: (value, ctx) => {
          if (effectiveTotal === 0) return "";
          return value > 0 ? `${value.toLocaleString()}` : "";
        }
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            if (effectiveTotal === 0) return " No wallet transactions";
            if (ctx.label === 'Service Wallet') return ` ${ctx.label}: ${ctx.parsed.toLocaleString()} Services`;
            return ` ${ctx.label}: ${ctx.parsed.toLocaleString()}`;
          }
        }
      }
    }
  };

  return (
    <div className="summary-split-card card-vibrant">
      <div className="summary-card-header">
        <div className="header-icon-circle" style={{
          background: 'linear-gradient(135deg,#10b981,#059669,#047857)',
          boxShadow: '0 10px 25px rgba(16,185,129,0.35)'
        }}>
          <Icon icon="solar:wallet-bold-duotone" />
        </div>
        <span className="summary-card-title">Wallet Summary</span>
      </div>
      <div className="summary-card-body">
        {/* Left: Total + breakdown */}
        <div className="summary-card-left">
          <div className="summary-total-label">TOTAL WALLET</div>
          <div className="summary-total-value">{effectiveTotal.toLocaleString()}</div>
          <div className="summary-breakdown">
            {[
              {
                label: 'Cash Wallet',
                val: cashWallet,
                icon: 'fluent-emoji:money-bag',
                color: '#10b981'
              },
              {
                label: 'Service Wallet',
                val: serviceWallet,
                icon: 'fluent-emoji:sparkles',
                color: '#8b5cf6'
              }
            ].map(item => (
              <div className="summary-breakdown-row" key={item.label}>
                <div className="summary-item-left">
                  <div
                    className="summary-mini-icon"
                    style={{ background: item.color }}
                  >
                    <Icon icon={item.icon} />
                  </div>
                  <span>{item.label}</span>
                </div>
                <span>{item.label === 'Service Wallet' ? `${item.val.toLocaleString()} Services` : item.val.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Center: Doughnut */}
        <div className="summary-donut-wrap">
          <div className="summary-donut-canvas">
            <Doughnut data={donutData} options={donutOptions} />
            <div className="donut-center-overlay">
              <span className="donut-center-total">{effectiveTotal.toLocaleString()}</span>
              <span className="donut-center-sub">Total</span>
            </div>
          </div>
        </div>

        {/* Right: Legend */}
        <div className="summary-legend">
          {[
            { label: 'Cash Wallet', pct: cashPct, color: '#10b981' },
            { label: 'Service Wallet', pct: servicePct, color: '#8b5cf6' },
          ].map(item => (
            <div className="summary-legend-item" key={item.label}>
              <span className="legend-dot" style={{ background: item.color }}></span>
              <span className="legend-label">{item.label}</span>
              <span className="legend-pct">{item.pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Payment Mode Card ─────────────────────────────────────────────── */
const PaymentModeCard = ({ mode, count, amount }) => {
  const formatMode = (m) => m.replace(/_/g, ' ');
  const getIcon = (m) => {
    const mm = m.toUpperCase();
    if (mm.includes('CASH')) return 'fluent-emoji:money-bag';
    if (mm.includes('CARD')) return 'fluent-emoji:credit-card';
    if (mm.includes('UPI') || mm.includes('PAY') || mm.includes('PHONE') || mm.includes('ONLINE')) return 'fluent-emoji:mobile-phone';
    if (mm.includes('CHEQUE')) return 'fluent-emoji:memo';
    return 'fluent-emoji:bank';
  };
  const getBg = (m) => {
    const mm = m.toUpperCase();
    if (mm.includes('CASH')) return 'green-soft-bg';
    if (mm.includes('CARD')) return 'blue-soft-bg';
    if (mm.includes('CHEQUE')) return 'orange-soft-bg';
    if (mm.includes('UPI') || mm.includes('PAY') || mm.includes('PHONE') || mm.includes('ONLINE')) return 'cyan-soft-bg';
    return 'purple-soft-bg';
  };
  return (
    <div className="performance-metric-card neat-card payment-mode-tile">
      <div className={`tile-icon-box ${getBg(mode)}`}>
        <Icon icon={getIcon(mode)} style={{ fontSize: '48px' }} />
      </div>
      <span className="metric-card-value"> {(amount || 0).toLocaleString()}</span>
      <span className="metric-card-label">{formatMode(mode)}</span>
      <span className="metric-card-subvalue">{count || 0} Transactions</span>
    </div>
  );
};

/* ── Dashboard Page ────────────────────────────────────────────────── */

const getRemainingDaysIST = (finalExpiryDate) => {
  if (!finalExpiryDate) return null;
  const options = { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' };
  const nowISTStr = new Date().toLocaleDateString('en-CA', options); // YYYY-MM-DD
  const expiryISTStr = new Date(finalExpiryDate).toLocaleDateString('en-CA', options); // YYYY-MM-DD
  const [nY, nM, nD] = nowISTStr.split('-').map(Number);
  const [eY, eM, eD] = expiryISTStr.split('-').map(Number);
  const nowMs = Date.UTC(nY, nM - 1, nD);
  const expMs = Date.UTC(eY, eM - 1, eD);
  return Math.round((expMs - nowMs) / (1000 * 60 * 60 * 24));
};

const getDynamicSubscriptionStatus = (status, finalExpiryDate) => {
  if (status === 'Expired') {
    return { text: 'EXPIRED', className: 'expired' };
  }
  if (status === 'Renewed' || status === 'Cancelled') {
    return { text: status.toUpperCase(), className: status.toLowerCase() };
  }
  if (!finalExpiryDate) {
    return { text: status?.toUpperCase() || 'UNKNOWN', className: status?.toLowerCase() || '' };
  }

  const remainingDays = getRemainingDaysIST(finalExpiryDate);

  if (remainingDays !== null && remainingDays <= 0) {
    return { text: 'EXPIRED', className: 'expired' };
  } else if (remainingDays === 1) {
    return { text: 'EXPIRES TOMORROW', className: 'expires-tomorrow' };
  } else if (remainingDays <= 7) {
    return { text: `EXPIRING IN ${remainingDays} DAYS`, className: 'expiring-7' };
  } else if (remainingDays <= 15) {
    return { text: `EXPIRING IN ${remainingDays} DAYS`, className: 'expiring-15' };
  } else {
    return { text: 'ACTIVE', className: 'active' };
  }
};

const DashboardPage = ({ sessionToken: propToken }) => {
  const navigate = useNavigate();
  const sessionToken = propToken || localStorage.getItem("Token");
  const role = localStorage.getItem("role");

  const adminStoreIds = (() => {
    try {
      const parsed = JSON.parse(localStorage.getItem("storeIds") || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  })();

  const defaultStoreId =
    localStorage.getItem("currentStoreId") ||
    sessionStorage.getItem("currentStoreId") ||
    adminStoreIds[0] ||
    "";

  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('daily');
  const [loading, setLoading] = useState(false);
  const [recentAppointments, setRecentAppointments] = useState([]);
  const { selectedStoreId } = useStore();
  const [store, setStore] = useState(
    role === "SelfAdmin" && defaultStoreId
      ? {
        value: defaultStoreId,
        label: "Loading Store..."
      }
      : ALL_STORE_OPTION
  );
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showPostponeModal, setShowPostponeModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [postponeDate, setPostponeDate] = useState('');
  const [postponeTime, setPostponeTime] = useState('');
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const { subscriptionData, setSubscriptionData, openRenewPackageModal } = useSubscriptionRenewal();
  const [showSubscriptionCard, setShowSubscriptionCard] = useState(false);

  const handleSalesAnalyticsCardClick = () => {
    if (filter === 'custom' && (!startDate || !endDate)) {
      toast.info("Please select date range");
      return;
    }
    let queryParam = 'today';
    if (filter === 'daily') {
      queryParam = 'today';
    } else if (filter === 'weekly') {
      queryParam = 'last7days';
    } else if (filter === 'monthly') {
      queryParam = 'currentMonth';
    } else if (filter === 'yearly') {
      queryParam = 'currentFY';
    } else if (filter === 'custom') {
      navigate(`/reports/summary?dateRange=custom&fromDate=${startDate}&toDate=${endDate}`);
      return;
    } else {
      queryParam = filter;
    }
    navigate(`/reports/summary?dateRange=${queryParam}`);
  };

  const handleCustomerCardClick = (tabKey = 'all') => {
    if (filter === 'custom' && (!startDate || !endDate)) {
      toast.info("Please select date range");
      return;
    }
    let queryParam = 'today';
    if (filter === 'daily') {
      queryParam = 'today';
    } else if (filter === 'weekly') {
      queryParam = 'last7days';
    } else if (filter === 'monthly') {
      queryParam = 'currentMonth';
    } else if (filter === 'yearly') {
      queryParam = 'currentFY';
    } else if (filter === 'custom') {
      navigate(`/reports/customer?dateRange=custom&fromDate=${startDate}&toDate=${endDate}&tab=${tabKey}`);
      return;
    } else {
      queryParam = filter;
    }
    navigate(`/reports/customer?dateRange=${queryParam}&tab=${tabKey}`);
  };

  const handleStaffPerformanceClick = () => {
    if (filter === 'custom' && (!startDate || !endDate)) {
      toast.info("Please select date range");
      return;
    }
    let queryParam = 'today';
    if (filter === 'daily') {
      queryParam = 'today';
    } else if (filter === 'weekly') {
      queryParam = 'last7days';
    } else if (filter === 'monthly') {
      queryParam = 'currentMonth';
    } else if (filter === 'yearly') {
      queryParam = 'currentFY';
    } else if (filter === 'custom') {
      navigate(`/reports/staff-sales?dateRange=custom&fromDate=${startDate}&toDate=${endDate}`);
      return;
    } else {
      queryParam = filter;
    }
    navigate(`/reports/staff-sales?dateRange=${queryParam}`);
  };

  const handleSalesReportClick = () => {
    if (filter === 'custom' && (!startDate || !endDate)) {
      toast.info("Please select date range");
      return;
    }
    let queryParam = 'today';
    if (filter === 'daily') {
      queryParam = 'today';
    } else if (filter === 'weekly') {
      queryParam = 'last7days';
    } else if (filter === 'monthly') {
      queryParam = 'currentMonth';
    } else if (filter === 'yearly') {
      queryParam = 'currentFY';
    } else if (filter === 'custom') {
      navigate(`/reports/sales?dateRange=custom&fromDate=${startDate}&toDate=${endDate}`);
      return;
    } else {
      queryParam = filter;
    }
    navigate(`/reports/sales?dateRange=${queryParam}`);
  };



  useEffect(() => {
    const hasVisited = sessionStorage.getItem("dashboard_visited");
    if (!hasVisited) {
      sessionStorage.setItem("dashboard_visited", "true");
      window.location.reload();
    }
  }, []);


  useEffect(() => {
    const fetchSelectedStoreDetails = async () => {
      const storeIdToFetch = store?.value;
      if (!storeIdToFetch || storeIdToFetch === "all") return;
      try {
        const response = await getData(`stores/store/${storeIdToFetch}`, {}, sessionToken);
        if (response?.status === 200 && response?.data) {
          const storeData = response.data;
          if (storeData?._id && storeData?.storeName) {
            setStore({
              value: storeData._id,
              label: storeData.storeName
            });
          }
        }
      } catch (error) {
        console.error("Failed to fetch store details", error);
      }
    };

    if (store?.value && store?.label === "Loading Store...") {
      fetchSelectedStoreDetails();
    }
  }, [store?.value, store?.label, sessionToken]);

  useEffect(() => {
    if (filter === 'custom' && (!startDate || !endDate)) {
      setSummary(null);
      fetchRecentAppointments();
      return;
    }
    fetchDashboardSummary();
    fetchRecentAppointments();
  }, [selectedStoreId, store?.value, filter, startDate, endDate]);

  const fetchDashboardSummary = async () => {
    if (filter === 'custom' && (!startDate || !endDate)) {
      setSummary(null);
      return;
    }
    try {
      setLoading(true);

      let effectiveStoreId = "";

      if (role === "SelfAdmin") {
        effectiveStoreId =
          store?.value ||
          localStorage.getItem("currentStoreId") ||
          sessionStorage.getItem("currentStoreId") ||
          adminStoreIds[0] ||
          "";

        if (!effectiveStoreId) {
          console.warn("SelfAdmin Dashboard: Store ID not available");
          return;
        }
      } else if (
        ["SuperAdmin", "Manager", "Accountant"].includes(role)
      ) {
        effectiveStoreId =
          store?.value ||
          selectedStoreId ||
          "";
      } else {
        effectiveStoreId = selectedStoreId || "";
      }

      const params = {
        storeId: effectiveStoreId
      };
      if (filter === 'custom' && startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      const response = await getData('dashboards/dashboard/summary', params, sessionToken);
      if (response?.status !== 200) {
        toast.error(response?.message || "Failed to fetch Dashboard Summary");
        return;
      }
      setSummary(response?.data || {});
    } catch (error) {
      toast.error(error.message || "Failed to fetch Dashboard Summary");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentAppointments = async () => {
    try {
      let sid = "";
      if (role === "SuperAdmin" || role === "SelfAdmin") {
        sid =
          store?.value ||
          localStorage.getItem("currentStoreId") ||
          sessionStorage.getItem("currentStoreId") ||
          adminStoreIds[0] ||
          "";
      } else {
        sid = selectedStoreId || "";
      }

      const params = {
        page: 1,
        limit: 50,
        sort: JSON.stringify([['createdAt', -1]]),
        status: ["Pending", "Postponed"]
      };
      if (sid && sid !== 'all') params.storeId = sid;
      const response = await getData('appointmentbookings/appointmentbookings', params, sessionToken);
      const resultsWrapper = response?.data?.[0];
      setRecentAppointments(resultsWrapper?.paginatedResults || []);
    } catch (error) {
      console.error("Failed to fetch recent appointments", error);
    }
  };

  const handleStatusChange = async (bookingId, newStatus, app = null) => {
    if (newStatus === "Postponed") {
      setSelectedAppointment(app);
      setShowPostponeModal(true);
      return;
    }
    try {
      const response = await putData(
        `appointmentbookings/appointmentbooking/${bookingId}`,
        { status: newStatus },
        sessionToken
      );
      if (response?.status === 200 || response?.success) {
        toast.success("Appointment status updated");
        fetchRecentAppointments();
      } else {
        toast.error(response?.message || "Failed to update status");
      }
    } catch (error) {
      toast.error("Error updating status");
    }
  };

  const handlePostponeUpdate = async () => {
    if (!postponeDate) { toast.error("Please select postpone date"); return; }
    try {
      const updatedDateTime = new Date(`${postponeDate}T${postponeTime}`);
      const payload = { status: "Postponed", appointmentDate: postponeDate, timeSlot: updatedDateTime };
      const response = await putData(
        `appointmentbookings/appointmentbooking/${selectedAppointment?._id}`,
        payload,
        sessionToken
      );
      if (response?.status === 200 || response?.success) {
        toast.success("Appointment postponed successfully");
        setShowPostponeModal(false);
        setSelectedAppointment(null);
        setPostponeDate('');
        fetchRecentAppointments();
      } else {
        toast.error(response?.message || "Failed to postpone appointment");
      }
    } catch (error) {
      toast.error("Error updating appointment");
    }
  };

  const timeKey = (() => {
    const map = { daily: 'today', weekly: 'week', monthly: 'month', yearly: 'year', custom: 'custom' };
    return map[filter] || 'today';
  })();

  const isCustomDatePending =
    filter === "custom" &&
    (!startDate || !endDate);

  const data = isCustomDatePending
    ? null
    : (summary?.[timeKey] || {});
  console.log(summary?.[timeKey]);
  const topProducts = summary?.topProducts?.[timeKey] || [];
  const topServices = summary?.topServices?.[timeKey] || [];
  const salesData = summary?.salesChart?.[timeKey] || [];
  const topCat = summary?.topCategories?.[timeKey] || { labels: [], values: [] };

  const totalPayments = (data?.totalCash || 0) + (data?.totalUPI || 0) + (data?.totalCard || 0) + (data?.totalWallet || 0) + (data?.totalOthers || 0);

  /* Chart labels helper */
  const getChartLabels = (key) => {
    if (key === 'today') return Array.from({ length: 24 }, (_, i) => `${i % 12 || 12} ${i < 12 ? 'AM' : 'PM'}`);
    if (key === 'week') return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    if (key === 'month') return Array.from({ length: 31 }, (_, i) => `${i + 1}`);
    if (key === 'custom') {
      if (startDate && endDate && startDate === endDate) {
        return Array.from({ length: 24 }, (_, i) => `${i % 12 || 12} ${i < 12 ? 'AM' : 'PM'}`);
      }
      const start = new Date(startDate), end = new Date(endDate), labels = [];
      while (start <= end) {
        labels.push(start.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }));
        start.setDate(start.getDate() + 1);
      }
      return labels;
    }
    return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  };

  /* Sales line chart */
  const lineData = {
    labels: getChartLabels(timeKey),
    datasets: [{
      label: 'Revenue',
      data: salesData,
      borderColor: '#ec4899',
      backgroundColor: 'rgba(236,72,153,0.08)',
      fill: true,
      tension: 0.4,
      pointRadius: 3,
      pointHoverRadius: 6,
      pointBackgroundColor: '#ec4899',
      borderWidth: 2,
    }]
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 1800, easing: 'easeOutQuart' },
    plugins: {
      legend: {
        display: false
      },

      tooltip: {
        backgroundColor: '#1a202c',
        titleFont: { size: 13, weight: 'bold' },
        bodyFont: { size: 12 },
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.y.toLocaleString()}`
        }
      },

      datalabels: {
        display: true,

        color: '#374151',

        font: {
          size: 11,
          weight: 'bold'
        },

        anchor: 'end',
        align: 'top',

        offset: 12,

        formatter: (value) => {
          return value > 0
            ? value.toLocaleString()
            : '';
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grace: '20%',
        grid: { color: 'rgba(0,0,0,0.04)', drawBorder: false },
        ticks: { color: '#64748b', font: { weight: '600', size: 10 } }
      },
      x: {
        grid: { display: false },
        ticks: { color: '#64748b', font: { weight: '600', size: 10 } }
      }
    }
  };

  /* Staff bar chart */
  const staffData = data?.staffPerformance || [];
  const staffChartData = {
    labels:
      staffData.length > 0
        ? staffData.map(
          (s) => s.employeeName || s.staffName || "N/A"
        )
        : ["No Data"],

    datasets: [
      {
        label: "Product",
        data:
          staffData.length > 0
            ? staffData.map((s) => s.productValue || 0)
            : [0],
        backgroundColor: "#06b6d4",
        borderRadius: 10,
        maxBarThickness: 50,
        categoryPercentage: 0.5,
        barPercentage: 0.7,
      },

      {
        label: "Service",
        data:
          staffData.length > 0
            ? staffData.map((s) => s.serviceValue || 0)
            : [0],
        backgroundColor: "#ec4899",
        borderRadius: 10,
        maxBarThickness: 50,
        categoryPercentage: 0.5,
        barPercentage: 0.7,
      },

      {
        label: "Membership",
        data:
          staffData.length > 0
            ? staffData.map((s) => s.membershipValue || 0)
            : [0],
        backgroundColor: "#7c3aed",
        borderRadius: 10,
        maxBarThickness: 50,
        categoryPercentage: 0.5,
        barPercentage: 0.7,
      },
    ],
  };

  const staffChartOptions = {
    responsive: true,
    maintainAspectRatio: false,

    layout: {
      padding: {
        top: 30,
      },
    },

    animation: {
      duration: 2200,
      easing: 'easeOutQuart',
    },

    plugins: {
      legend: {
        display: true,
        position: "bottom",

        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 20,

          font: {
            size: 12,
            weight: "600",
          },
        },
      },

      tooltip: {
        enabled: true,
        backgroundColor: '#3C0008',

        callbacks: {
          label: (ctx) =>
            `Revenue: ${ctx.parsed.y.toLocaleString()}`,
        },
      },

      datalabels: {
        anchor: 'end',
        align: 'top',

        formatter: (value) =>
          `${value.toLocaleString()}`,

        color: '#3C0008',

        font: {
          weight: 'bold',
          size: 13,
        },
      },
    },

    scales: {
      x: {
        grid: {
          display: false,
        },

        ticks: {
          color: '#475569',

          font: {
            weight: '700',
            size: 14,
          },
        },
      },

      y: {
        beginAtZero: true,

        grid: {
          color: 'rgba(0,0,0,0.05)',
        },

        ticks: {
          color: '#64748b',

          font: {
            weight: '700',
            size: 11,
          },
        },
      },
    },
  };

  /* Payment modes */
  const paymentModesData = ['CASH', 'CARD', 'UPI', 'OTHERS'].map(mode => {
    let count = 0, amount = 0;
    if (mode === 'CASH') {
      const md = data?.paymentModes?.['CASH'] || {};
      count = md.count || data?.cashCount || 0;
      amount = md.amount || data?.totalCash || 0;
    } else if (mode === 'CARD') {
      ['CARD', 'CREDIT_CARD', 'DEBIT_CARD'].forEach(m => {
        const md = data?.paymentModes?.[m] || {};
        count += md.count || 0; amount += md.amount || 0;
      });
    } else if (mode === 'UPI') {
      ['UPI', 'GOOGLE PAY', 'PHONEPE', 'PAYTM', 'ONLINE_PAYMENT'].forEach(m => {
        const md = data?.paymentModes?.[m] || {};
        count += md.count || 0; amount += md.amount || 0;
      });
    } else if (mode === 'OTHERS') {
      const catchAll = ['OTHERS', 'CHEQUE'];
      Object.keys(data?.paymentModes || {}).forEach(m => {
        if (!['CASH', 'CARD', 'CREDIT_CARD', 'DEBIT_CARD', 'UPI', 'GOOGLE PAY', 'PHONEPE', 'PAYTM', 'ONLINE_PAYMENT', 'OTHERS', 'CHEQUE'].includes(m)) catchAll.push(m);
      });
      catchAll.forEach(m => {
        const md = data?.paymentModes?.[m] || {};
        count += md.count || 0; amount += md.amount || 0;
      });
    }
    return { mode, count, amount };
  });

  /* Top Categories bar chart */
  const topCatChartData = {
    labels: topCat.labels,
    datasets: [{
      label: 'Count',
      data: topCat.values,
      backgroundColor: ['#7c3aed', '#06b6d4', '#ec4899', '#f59e0b', '#10b981'],
      borderRadius: 10,
      maxBarThickness: 42
    }]
  };

  const topCatOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: {
        color: "#000000",
      }
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#64748b', font: { weight: '600', size: 11 } } },
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { color: '#64748b', font: { weight: '600', size: 11 } } }
    }
  };

  const previousKeyMap = {
    today: 'previousToday',
    week: 'previousWeek',
    month: 'previousMonth',
    year: 'previousYear',
    custom: 'previousCustom',
  };

  const previousData = summary?.[previousKeyMap[timeKey]] || {};

  const getVsLabel = (growthVal) => {
    const labels = {
      today: 'Previous Day',
      week: 'Previous Week',
      month: 'Previous Month',
      year: 'Previous Year',
      custom: 'Previous Period',
    };
    const periodLabel = labels[timeKey] || 'Previous Period';
    return `${growthVal >= 0 ? '↑' : '↓'} ${Math.abs(growthVal)}% vs ${periodLabel}`;
  };

  const calculateGrowth = (current, previous) => {
    if (!previous || previous <= 0) {
      return 0;
    }

    return Math.min(
      100,
      Math.round((current / previous) * 100)
    );
  };

  const totalNetGrowth = calculateGrowth(
    data?.totalNet || 0,
    previousData?.totalNet || 0
  );

  const grossSalesGrowth = calculateGrowth(
    data?.totalSales || 0,
    previousData?.totalSales || 0
  );

  const discountGrowth = calculateGrowth(
    data?.totalDiscount || 0,
    previousData?.totalDiscount || 0
  );

  const gstGrowth = calculateGrowth(
    data?.totalGST || 0,
    previousData?.totalGST || 0
  );

  const invoiceGrowth = calculateGrowth(
    data?.noOfBills || 0,
    previousData?.noOfBills || 0
  );

  const productCountGrowth = calculateGrowth(
    Number(data?.productCount || 0),
    Number(previousData?.productCount || 0)
  );

  const productGrossGrowth = calculateGrowth(
    Number(data?.productGross || 0),
    Number(previousData?.productGross || 0)
  );

  const invoiceCount =
    Number(data?.serviceBills || 0) +
    Number(data?.productBills || 0) +
    Number(data?.mixedBills || 0) +
    Number(data?.cancelledBills || 0);

  return (
    <div className="dashboard-layout">
      <div className="dashboard-main">

        {/* ── Store Subscription Details Card (Displayed only when remaining days <= 15 or Expired) ── */}

        {(() => {
          if (!subscriptionData) return null;
          const calculatedRemainingDays = getRemainingDaysIST(subscriptionData.finalExpiryDate) ?? subscriptionData.remainingDays;
          const isExpired = subscriptionData.subscriptionStatus === 'Expired' || (calculatedRemainingDays !== null && calculatedRemainingDays <= 0);
          const shouldShowOnDashboard = isExpired || (calculatedRemainingDays !== null && calculatedRemainingDays <= 15);

          if (!shouldShowOnDashboard) return null;

          return (
            <div className="subscription-details-card neat-card">
              <div className="sub-card-header">
                <div className="sub-card-title">
                  <Icon icon="solar:card-search-bold-duotone" className="sub-card-title-icon" />
                  <div>
                    <h3>Store Subscription Details</h3>
                    <p>Current active package & validity overview</p>
                  </div>
                </div>
                {(() => {
                  const showRenewalAction = calculatedRemainingDays !== null && (calculatedRemainingDays <= 15 || isExpired);

                  if (showRenewalAction) {
                    return (
                      <button
                        className="sub-renew-sm-btn"
                        onClick={() => {
                          openRenewPackageModal();
                        }}
                      >
                        <Icon icon="solar:restart-bold-duotone" />
                        Renew Package
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>

            <div className="sub-card-grid">
              <div className="sub-grid-item">
                <span className="sub-item-label">Store</span>
                <span className="sub-item-value highlight">{subscriptionData.storeName || 'N/A'}</span>
              </div>
              <div className="sub-grid-item">
                <span className="sub-item-label">Current Package</span>
                <span className="sub-item-value pkg-badge">{subscriptionData.packageName || 'N/A'}</span>
              </div>
              <div className="sub-grid-item">
                <span className="sub-item-label">Package Price</span>
                <span className="sub-item-value">₹{(subscriptionData.packagePrice || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
              </div>
              <div className="sub-grid-item">
                <span className="sub-item-label">GST</span>
                <span className="sub-item-value">
                  {subscriptionData.isGstApplicable
                    ? `${subscriptionData.gstPercentage || 0}% ${subscriptionData.gstType || 'Exclusive'}`
                    : 'Exclusive'}
                </span>
              </div>
              <div className="sub-grid-item">
                <span className="sub-item-label">GST Amount</span>
                <span className="sub-item-value">
                  ₹{(() => {
                    let base = subscriptionData.packagePrice || 0;
                    let gst = subscriptionData.gstAmount;
                    if (gst === undefined) {
                      if (subscriptionData.isGstApplicable && subscriptionData.gstType === 'Exclusive') {
                        gst = (base * (subscriptionData.gstPercentage || 0)) / 100;
                      } else if (subscriptionData.isGstApplicable && subscriptionData.gstType === 'Inclusive') {
                        gst = base - (base / (1 + (subscriptionData.gstPercentage || 0) / 100));
                      } else {
                        gst = 0;
                      }
                    }
                    return (gst || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
                  })()}
                </span>
              </div>
              <div className="sub-grid-item">
                <span className="sub-item-label">Total Amount</span>
                <span className="sub-item-value total-highlight">
                  ₹{(() => {
                    let base = subscriptionData.packagePrice || 0;
                    let gst = subscriptionData.gstAmount;
                    let total = subscriptionData.totalAmount;
                    if (total === undefined) {
                      if (subscriptionData.isGstApplicable && subscriptionData.gstType === 'Exclusive') {
                        gst = (base * (subscriptionData.gstPercentage || 0)) / 100;
                        total = base + gst;
                      } else if (subscriptionData.isGstApplicable && subscriptionData.gstType === 'Inclusive') {
                        total = base;
                      } else {
                        total = base;
                      }
                    }
                    return (total || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
                  })()}
                </span>
              </div>
              <div className="sub-grid-item">
                <span className="sub-item-label">Subscription Start Date</span>
                <span className="sub-item-value">
                  {subscriptionData.validityStartDate
                    ? new Date(subscriptionData.validityStartDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                    : 'N/A'}
                </span>
              </div>
              <div className="sub-grid-item">
                <span className="sub-item-label">Base Validity</span>
                <span className="sub-item-value">{subscriptionData.baseValidity} {subscriptionData.validityUnit}</span>
              </div>
              <div className="sub-grid-item">
                <span className="sub-item-label">Extra Validity / Offer</span>
                <span className="sub-item-value">
                  {subscriptionData.extraValidity > 0
                    ? `${subscriptionData.extraValidity} ${subscriptionData.extraValidityUnit || 'Days'}`
                    : 'None'}
                </span>
              </div>
              {subscriptionData.totalGraceDays > 0 && (
                <div className="sub-grid-item">
                  <span className="sub-item-label">Grace Period</span>
                  <span className="sub-item-value" style={{ color: '#b45309', fontWeight: 600 }}>
                    {subscriptionData.totalGraceDays} Days
                  </span>
                </div>
              )}
              <div className="sub-grid-item">
                <span className="sub-item-label">Final Expiry Date</span>
                <span className="sub-item-value expiry-highlight">
                  {subscriptionData.finalExpiryDate
                    ? new Date(subscriptionData.finalExpiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                    : 'N/A'}
                </span>
              </div>
              <div className="sub-grid-item">
                <span className="sub-item-label">Subscription Status</span>
                {(() => {
                  const { text, className } = getDynamicSubscriptionStatus(subscriptionData.subscriptionStatus, subscriptionData.finalExpiryDate);
                  return (
                    <span className={`sub-status-pill ${className}`}>
                      {text}
                    </span>
                  );
                })()}
              </div>
            </div>
          </div>
          );
        })()}

        {subscriptionData?.upcomingSubscription && (showSubscriptionCard || role === 'StoreAdmin') && (
          <div className="subscription-details-card neat-card" style={{ marginTop: '20px', marginBottom: '28px', borderLeft: '4px solid #f59e0b' }}>
            <div className="sub-card-header">
              <div className="sub-card-title">
                <Icon icon="solar:clock-circle-bold-duotone" className="sub-card-title-icon" style={{ color: '#f59e0b' }} />
                <div>
                  <h3>Upcoming Renewal Package</h3>
                  <p>This package will automatically activate after your current subscription expires</p>
                </div>
              </div>
            </div>
            <div className="sub-card-grid">
              <div className="sub-grid-item">
                <span className="sub-item-label">Package Name</span>
                <span className="sub-item-value pkg-badge" style={{ backgroundColor: '#fef3c7', color: '#b45309' }}>
                  {subscriptionData.upcomingSubscription.packageName || 'N/A'}
                </span>
              </div>
              <div className="sub-grid-item">
                <span className="sub-item-label">Package Price</span>
                <span className="sub-item-value">₹{(subscriptionData.upcomingSubscription.packagePrice || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
              </div>
              <div className="sub-grid-item">
                <span className="sub-item-label">Will Activate On</span>
                <span className="sub-item-value" style={{ fontWeight: 600 }}>
                  {subscriptionData.upcomingSubscription.validityStartDate
                    ? new Date(subscriptionData.upcomingSubscription.validityStartDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                    : 'N/A'}
                </span>
              </div>
              <div className="sub-grid-item">
                <span className="sub-item-label">Validity</span>
                <span className="sub-item-value">{subscriptionData.upcomingSubscription.baseValidity} {subscriptionData.upcomingSubscription.validityUnit}</span>
              </div>
              <div className="sub-grid-item">
                <span className="sub-item-label">Will Expire On</span>
                <span className="sub-item-value expiry-highlight" style={{ backgroundColor: '#f3f4f6', color: '#374151' }}>
                  {subscriptionData.upcomingSubscription.finalExpiryDate
                    ? new Date(subscriptionData.upcomingSubscription.finalExpiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                    : 'N/A'}
                </span>
              </div>
              <div className="sub-grid-item">
                <span className="sub-item-label">Status</span>
                <span className="sub-status-pill" style={{ backgroundColor: '#fef3c7', color: '#b45309' }}>
                  UPCOMING
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── Header Row (Date Filter Bar) ───────────────────── */}
        <div className="dashboard-header-row">
          <div className="filter-buttons desktop-only">
            {['daily', 'weekly', 'monthly', 'yearly', 'custom'].map((f) => (
              <button
                key={f}
                className={'filter-btn ' + (filter === f ? 'active' : '')}
                onClick={() => {
                  setFilter(f);

                  if (f !== "custom") {
                    setStartDate("");
                    setEndDate("");
                  }

                  if (f === "custom") {
                    toast.info("Please select date range");
                  }
                }}
              >
                {f === 'daily' ? 'Today' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          <div className="filter-dropdown-wrapper mobile-only">
            <button
              type="button"
              className="mobile-filter-trigger"
              onClick={() => setIsBottomSheetOpen(true)}
            >
              <Icon icon="solar:filter-bold-duotone" className="filter-trigger-icon" />
              <span>{filter === 'daily' ? 'Today' : filter.charAt(0).toUpperCase() + filter.slice(1)}</span>
            </button>
          </div>

          {isBottomSheetOpen && (
            <div className="bottom-sheet-overlay" onClick={() => setIsBottomSheetOpen(false)}>
              <div className="bottom-sheet-content" onClick={(e) => e.stopPropagation()}>
                <div className="bottom-sheet-drag-handle"></div>
                <div className="bottom-sheet-header">
                  <h3>Select Filter</h3>
                  <button type="button" className="close-sheet-btn" onClick={() => setIsBottomSheetOpen(false)}>
                    <Icon icon="solar:close-circle-bold-duotone" />
                  </button>
                </div>
                <div className="bottom-sheet-options">
                  {[
                    { value: 'daily', label: 'Today' },
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'yearly', label: 'Yearly' },
                    { value: 'custom', label: 'Custom' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`sheet-option-btn ${filter === opt.value ? 'active' : ''}`}
                      onClick={() => {
                        setFilter(opt.value);
                        if (opt.value !== "custom") {
                          setStartDate("");
                          setEndDate("");
                        }
                        if (opt.value === "custom") {
                          toast.info("Please select date range");
                        }
                        setIsBottomSheetOpen(false);
                      }}
                    >
                      <span>{opt.label}</span>
                      {filter === opt.value && <Icon icon="solar:check-circle-bold-duotone" className="checked-icon" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {filter === 'custom' && (
            <div className="custom-date-range">

              <input
                type="date"
                className="date-input glossy-input"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);

                  if (endDate && e.target.value > endDate) {
                    setEndDate('');
                    toast.error("Please select End Date again");
                  }
                }}
              />

              <span className="date-separator">to</span>

              <input
                type="date"
                className="date-input glossy-input"
                value={endDate}
                onChange={(e) => {
                  const selectedEndDate = e.target.value;

                  if (startDate && selectedEndDate < startDate) {
                    toast.error("End Date cannot be earlier than Start Date");
                    return;
                  }

                  setEndDate(selectedEndDate);
                }}
              />

              {(startDate || endDate) && (
                <button
                  className="clear-date-btn"
                  title="Clear Dates"
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                    toast.info("Please select date range");
                  }}
                >
                  <Icon icon="solar:close-circle-bold-duotone" />
                </button>
              )}

            </div>
          )}

          {["SuperAdmin", "SelfAdmin", "Manager", "Accountant"].includes(role) && (
            <StoreSingleAllStoresSelect value={store} onChange={(s) => setStore(s)} />
          )}
        </div>




        <div className="sales-analytics-wrapper">

          <div className="sales-analytics-header">

            <div className="sales-analytics-icon">
              <Icon icon="solar:chart-square-bold-duotone" />
            </div>

            <div>
              <h2>Sales Analytics</h2>
              <p>Overview of your business performance</p>
            </div>

          </div>

          {/* ── Top KPI Tiles (7 cards) ──────── */}
          <div className="top-kpi-grid">
            <div className="top-kpi-cards">

              <MetricCard
                label="TOTAL NET SALES"
                value={` ${Number((data?.totalSales || 0) - (data?.totalGST || 0)).toLocaleString('en-IN', {
                  maximumFractionDigits: 0
                })}`}
                subValue={getVsLabel(totalNetGrowth)}
                icon="fluent-emoji:money-with-wings"
                colorClass="green-bg"
                onClick={handleSalesAnalyticsCardClick}
              />

              <MetricCard
                label="GROSS SALES"
                value={` ${Number(data?.totalSales || 0).toLocaleString('en-IN', {
                  maximumFractionDigits: 0
                })}`}
                subValue={getVsLabel(grossSalesGrowth)}
                icon="fluent-emoji:money-bag"
                colorClass="yellow-bg"
                onClick={handleSalesAnalyticsCardClick}
              />

              <MetricCard
                label="TOTAL DISCOUNT"
                value={` ${Number(data?.totalDiscount || 0).toLocaleString('en-IN', {
                  maximumFractionDigits: 0
                })}`}
                subValue={getVsLabel(discountGrowth)}
                icon="fluent-emoji:label"
                colorClass="orange-bg"
                onClick={handleSalesAnalyticsCardClick}
              />

              <MetricCard
                label="TOTAL GST / TAX"
                value={` ${Number(data?.totalGST || 0).toLocaleString('en-IN', {
                  maximumFractionDigits: 0
                })}`}
                subValue={getVsLabel(gstGrowth)}
                icon="fluent-emoji:receipt"
                colorClass="pink-bg"
                onClick={handleSalesAnalyticsCardClick}
              />

              <MetricCard
                label="INVOICE COUNT"
                value={data?.noOfBills || 0}
                subValue={getVsLabel(invoiceGrowth)}
                icon="fluent-emoji:memo"
                colorClass="purple-bg"
                onClick={handleSalesAnalyticsCardClick}
              />

              <MetricCard
                label="CANCELLED INVOICES"

                value={data?.cancelledBills || 0}

                subValue="Cancelled Bills"

                icon="fluent-emoji:wastebasket"

                colorClass="red-bg"
                onClick={handleSalesAnalyticsCardClick}
              />

              <MetricCard
                label="PRODUCT COUNT"
                value={` ${Number(data?.productCount || 0).toLocaleString('en-IN', {
                  maximumFractionDigits: 0
                })}`}
                subValue={getVsLabel(productCountGrowth)}
                icon="fluent-emoji:shopping-bags"
                colorClass="blue-bg"
                onClick={handleSalesAnalyticsCardClick}
              />

              <MetricCard
                label="PRODUCT GROSS"
                value={` ${Number(data?.productGross || 0).toLocaleString('en-IN', {
                  maximumFractionDigits: 0
                })}`}
                subValue={getVsLabel(productGrossGrowth)}
                icon="fluent-emoji:money-mouth-face"
                colorClass="dark-bg"
                onClick={handleSalesAnalyticsCardClick}
              />

            </div>
          </div>
        </div>

        {/* ── Payments & Wallet Summary ───── */}
        <div className="summary-row">
          <PaymentSummaryCard total={totalPayments} data={data} onClick={handleSalesAnalyticsCardClick} />
          <WalletSummaryCard data={data} />
        </div>

        {/* ── Customer Analytics + Top Categories ── */}
        <div className="performance-section-row">
          <div className="section-container card-vibrant">
            <div className="section-header">
              <h3><div className="header-icon-circle"><Icon icon="solar:users-group-two-rounded-bold-duotone" /></div> Customer Analytics</h3>
            </div>
            <div className="metrics-grid customer-grid">
              <div
                className="performance-metric-card neat-card"
                onClick={() => handleCustomerCardClick('men')}
                style={{ cursor: 'pointer' }}
              >
                <div className="customer-avatar-icon male-avatar">
                  <img
                    src="https://cdn-icons-png.flaticon.com/512/4140/4140048.png"
                    alt="Male"
                  />
                </div>

                <span className="metric-card-value">
                  {data?.men || 0}
                </span>

                <span className="metric-card-label">
                  MALE
                </span>

                <span className="metric-card-subvalue">
                  Total: {(data?.menAmount || 0).toLocaleString()}
                </span>
              </div>

              <div
                className="performance-metric-card neat-card"
                onClick={() => handleCustomerCardClick('women')}
                style={{ cursor: 'pointer' }}
              >
                <div className="customer-avatar-icon female-avatar">
                  <img
                    src="https://cdn-icons-png.flaticon.com/512/4140/4140051.png"
                    alt="Female"
                  />
                </div>

                <span className="metric-card-value">
                  {data?.women || 0}
                </span>

                <span className="metric-card-label">
                  FEMALE
                </span>

                <span className="metric-card-subvalue">
                  Total: {(data?.womenAmount || 0).toLocaleString()}
                </span>
              </div>

              <div
                className="performance-metric-card neat-card"
                onClick={() => handleCustomerCardClick('others')}
                style={{ cursor: 'pointer' }}
              >
                <div className="customer-avatar-icon others-avatar">
                  <img
                    src="https://cdn-icons-png.flaticon.com/512/681/681494.png"
                    alt="Others"
                  />
                </div>

                <span className="metric-card-value">
                  {data?.othersGender || 0}
                </span>

                <span className="metric-card-label">
                  OTHERS
                </span>

                <span className="metric-card-subvalue">
                  Total: {(data?.othersAmount || 0).toLocaleString()}
                </span>
              </div>
              <div
                className="performance-metric-card neat-card"
                onClick={() => handleCustomerCardClick('new')}
                style={{ cursor: 'pointer' }}
              >
                <div className="tile-icon-box cyan-soft-bg"><Icon icon="fluent-emoji:sparkles" style={{ fontSize: '40px' }} /></div>
                <span className="metric-card-value">{data?.newCustomers || 0}</span>
                <span className="metric-card-label">NEW CUSTOMERS</span>
                <span className="metric-card-subvalue">Total: {(data?.newCustomerAmount || 0).toLocaleString()}</span>
              </div>
              <div
                className="performance-metric-card neat-card"
                onClick={() => handleCustomerCardClick('old')}
                style={{ cursor: 'pointer' }}
              >
                <div className="tile-icon-box orange-soft-bg"><Icon icon="fluent-emoji:handshake" style={{ fontSize: '40px' }} /></div>
                <span className="metric-card-value">{data?.oldCustomers || 0}</span>
                <span className="metric-card-label">OLD CUSTOMERS</span>
                <span className="metric-card-subvalue">Total: {(data?.oldCustomerAmount || 0).toLocaleString()}</span>
              </div>
              <div
                className="performance-metric-card neat-card"
                onClick={() => handleCustomerCardClick('inactive')}
                style={{ cursor: 'pointer' }}
              >
                <div className="tile-icon-box orange-soft-bg"><Icon icon="fluent-emoji:heart-on-fire" style={{ fontSize: '40px' }} /></div>
                <span className="metric-card-value">{data?.churnedCustomers || 0}</span>
                <span className="metric-card-label">CHURNED CUSTOMERS</span>
                <span className="metric-card-subvalue">Billed &lt; 1 Month</span>
              </div>
              <div
                className="performance-metric-card neat-card"
                onClick={() => handleCustomerCardClick('inactive')}
                style={{ cursor: 'pointer' }}
              >
                <div className="tile-icon-box maroon-soft-bg"><Icon icon="fluent-emoji:broken-heart" style={{ fontSize: '40px' }} /></div>
                <span className="metric-card-value">{data?.defectedCustomers || 0}</span>
                <span className="metric-card-label">DEFECTED CUSTOMERS</span>
                <span className="metric-card-subvalue">Inactive &gt; 6 Months</span>
              </div>
            </div>
          </div>

          <div className="card-vibrant">
            <div className="section-header">
              <h3><div className="header-icon-circle"><Icon icon="solar:pie-chart-2-bold-duotone" /></div> Top Categories</h3>
              <button className="view-all-btn" onClick={() => navigate('/reports')}>View All</button>
            </div>
            <div className="chart-container">
              <Bar data={topCatChartData} options={topCatOptions} />
            </div>
          </div>
        </div>
        {/* ── Full Width Staff Performance ── */}
        <div className="staff-performance-full-row">
          <div
            className="card-vibrant"
            onClick={handleStaffPerformanceClick}
            style={{ cursor: 'pointer' }}
          >
            <div className="section-header">
              <h3>
                <div className="header-icon-circle">
                  <Icon icon="solar:user-id-bold-duotone" />
                </div>
                Staff Performance
              </h3>

              <button
                className="view-all-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStaffPerformanceClick();
                }}
              >
                View All
              </button>
            </div>

            <div
              className="chart-container staff-performance-chart-full"
            >
              <Bar
                data={staffChartData}
                options={staffChartOptions}
              />
            </div>
          </div>
        </div>

        {/* ── Sales Statistics + Recent Appointments ── */}
        <div className="stats-appointments-row">
          <div
            className="card-vibrant"
            onClick={handleSalesReportClick}
            style={{ cursor: 'pointer' }}
          >
            <div className="section-header">
              <h3><div className="header-icon-circle"><Icon icon="solar:graph-bold-duotone" /></div> Sales Statistics</h3>
              <button
                className="view-all-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSalesReportClick();
                }}
              >
                View All
              </button>
            </div>
            <div className="chart-container">
              <Line data={lineData} options={lineOptions} />
            </div>
          </div>

          <div className="card-vibrant recent-appointments-card">
            <div className="section-header">
              <h3><div className="header-icon-circle"><Icon icon="solar:calendar-date-bold-duotone" /></div> Recent Appointments</h3>
              <button className="add-appointment-btn" onClick={() => navigate('/appointments', { state: { isAddBooking: true, fromDashboard: true } })}>
                <Icon icon="solar:add-circle-bold-duotone" /> + Add
              </button>
            </div>
            <div className="appointment-table-wrapper recent-appointments-scroll">
              <table className="appointment-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Service</th>
                    <th>Time</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {recentAppointments.length > 0 ? recentAppointments.map((app) => (
                    <tr key={app._id}>
                      <td>
                        <div style={{ fontWeight: '700' }}>{app.CustomerDetails?.[0]?.name || 'N/A'}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{app.CustomerDetails?.[0]?.phoneNumber}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: '700' }}>{app.ServiceDetails?.[0]?.serviceName || 'N/A'}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: '600', fontSize: '12px' }}>
                          {app.appointmentDate ? new Date(app.appointmentDate).toLocaleDateString('en-GB') : 'N/A'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                          {app.timeSlot ? new Date(app.timeSlot).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </td>
                      <td>
                        <select
                          className={`status-edit-select ${app.status?.toLowerCase().replace(' ', '')}`}
                          value={app.status}
                          onChange={(e) => handleStatusChange(app._id, e.target.value, app)}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Completed">Completed</option>
                          <option value="Cancelled">Cancelled</option>
                          <option value="Postponed">Postponed</option>
                          <option value="Expired">Expired</option>
                        </select>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <button
                            className="edit-appointment-btn"
                            title="Edit Appointment"
                            onClick={() => navigate('/appointments', {
                              state: { isEditBooking: true, bookingData: app, openEditForm: true }
                            })}
                          >
                            <Icon icon="solar:pen-bold-duotone" />
                          </button>
                          
                          {(() => {
                            const statusLower = app.status?.toLowerCase();
                            if (statusLower === 'cancelled' || statusLower === 'expired' || statusLower === 'rejected') {
                                return null;
                            }
                            return (
                                <button
                                  className="edit-appointment-btn"
                                  title={statusLower === 'completed' ? "Already Billed" : "Create Bill"}
                                  disabled={statusLower === 'completed'}
                                  onClick={(e) => {
                                      e.stopPropagation();
                                      if (statusLower === 'completed') {
                                          toast.info("This appointment is already billed/completed.");
                                      } else {
                                          navigate(`/pos?appointmentId=${app._id}`);
                                      }
                                  }}
                                  style={{
                                      background: statusLower === 'completed' ? '#059669' : '#3C0008',
                                      opacity: statusLower === 'completed' ? 0.6 : 1,
                                      cursor: statusLower === 'completed' ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  <Icon icon="mdi:receipt-text-plus-outline" />
                                </button>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: '#CBD5E0' }}>No Data</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── Top Products + Top Services ── */}
        <div className="bottom-lists-row">
          <div className="card-vibrant">
            <div className="section-header">
              <h3><div className="header-icon-circle"><Icon icon="solar:box-minimalistic-bold-duotone" /></div> Top Products</h3>
              <button className="view-all-btn" onClick={() => navigate('/products')}>View All</button>
            </div>
            {topProducts.length > 0 ? topProducts.map((item, idx) => (
              <div className="list-item-pro" key={idx}>
                <div className="item-img-container">
                  <img src={item?.image ? `${process.env.REACT_APP_API_URL}${item.image}` : '/assets/placeholder.png'} onError={(e) => { e.target.onerror = null; e.target.src = "/assets/placeholder.png"; }} className="item-img-pro" alt="" />
                  <div className="item-rank-badge">{idx + 1}</div>
                </div>
                <div className="item-main-info">
                  <span className="item-name-pro">{item?.product}</span>
                  <div className="item-meta-pro"><Icon icon="solar:cart-bold-duotone" /> {item?.quantitySold} Sold</div>
                </div>
                <div className="val-badge-pro"> {item?.totalValue?.toLocaleString()}</div>
              </div>
            )) : (
              <div className="no-data-pro">
                <Icon icon="solar:box-minimalistic-linear" className="no-data-icon" />
                <span>No high-demand products currently</span>
              </div>
            )}
          </div>

          <div className="card-vibrant">
            <div className="section-header">
              <h3><div className="header-icon-circle"><Icon icon="solar:star-bold-duotone" /></div> Top Services</h3>
              <button className="view-all-btn" onClick={() => navigate('/services')}>View All</button>
            </div>
            {topServices.length > 0 ? topServices.map((item, idx) => (
              <div className="list-item-pro" key={idx}>
                <div className="item-img-container">
                  <img src={item?.image ? `${process.env.REACT_APP_API_URL}${item.image}` : '/assets/placeholder.png'} onError={(e) => { e.target.onerror = null; e.target.src = "/assets/placeholder.png"; }} className="item-img-pro" alt="" />
                  <div className="item-rank-badge">{idx + 1}</div>
                </div>
                <div className="item-main-info">
                  <span className="item-name-pro">{item?.service}</span>
                  <div className="item-meta-pro"><Icon icon="solar:calendar-bold-duotone" /> {item?.quantitySold} Booked</div>
                </div>
                <div className="val-badge-pro"> {item?.totalValue?.toLocaleString()}</div>
              </div>
            )) : (
              <div className="no-data-pro">
                <Icon icon="solar:stars-minimalistic-linear" className="no-data-icon" />
                <span>No top-rated services found</span>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ── Postpone Modal ─────────────────── */}
      {showPostponeModal && (
        <div className="postpone-modal-overlay">
          <div className="postpone-modal">
            <h3>Postpone Appointment</h3>
            <div className="postpone-field">
              <label>Select Date</label>
              <input type="date" value={postponeDate} onChange={(e) => setPostponeDate(e.target.value)} className="postpone-date-input" />
            </div>
            <div className="postpone-field">
              <label>Select Time</label>
              <input type="time" value={postponeTime} onChange={(e) => setPostponeTime(e.target.value)} className="postpone-date-input" />
            </div>
            <div className="postpone-modal-actions">
              <button className="cancel-postpone-btn" onClick={() => { setShowPostponeModal(false); setPostponeDate(''); setPostponeTime(''); }}>Cancel</button>
              <button className="save-postpone-btn" onClick={handlePostponeUpdate}>Update</button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default DashboardPage;